import { useQuery } from '@apollo/client/react'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

export const CrossChannelsTakeawaysDocument = graphql(/* GraphQL */ `
  query CrossChannelsTakeaways($input: CrossChannelsTakeawaysInput!) {
    crossChannelsTakeaways(input: $input) {
      id
      title
      description
      category
      channelIds
      feedback
      ctas {
        label
        action
        variant
      }
      metadata {
        draftId
        createPostType
        postId
        prefillAiPrompt
      }
    }
  }
`)

type CrossChannelsTakeawaysData = ResultOf<
  typeof CrossChannelsTakeawaysDocument
>['crossChannelsTakeaways']

interface UseCrossChannelsTakeawaysOptions {
  organizationId: string
  /** Channels to draw takeaways from — the page's permission-filtered selection. */
  channelIds: ReadonlyArray<string>
  /** Skip the query entirely (e.g. the feature flag is off). */
  skip?: boolean
}

interface UseCrossChannelsTakeawaysResult {
  data: CrossChannelsTakeawaysData
  loading: boolean
}

export function useCrossChannelsTakeaways({
  organizationId,
  channelIds,
  skip = false,
}: UseCrossChannelsTakeawaysOptions): UseCrossChannelsTakeawaysResult {
  // Apollo keeps the last result when a query becomes skipped, so the same flag
  // that skips the request must also gate what this hook reports.
  const shouldSkip = skip || !organizationId || channelIds.length === 0
  const { data, loading } = useQuery(CrossChannelsTakeawaysDocument, {
    variables: { input: { organizationId, channelIds: [...channelIds] } },
    // Takeaways go stale from things that happen elsewhere — a draft published,
    // a post sent, a goal set — and the server drops and replaces them on read.
    // `cache-first` would serve the stale set on a client-side navigation and
    // never ask. Cached Takeaways still render immediately; the caller separates
    // "no data yet" from "refreshing", so this costs no skeleton.
    fetchPolicy: 'cache-and-network',
    // An empty selection returns no takeaways by contract, so don't ask.
    skip: shouldSkip,
  })

  return {
    data: shouldSkip ? [] : (data?.crossChannelsTakeaways ?? []),
    loading,
  }
}
